import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Search, FileText, Plus, Edit, Trash, Save, X } from 'lucide-react';
import BottomNav from '../../components/BottomNav.tsx';
import { WorkStation } from '../../Interfaces/workstation.ts';
import { Building } from '../../Interfaces/building.ts';
import { processDocNJMH } from '../../utils/workInstructionVMF.ts';
import { ProcessDoc } from '../../Interfaces/processDocs.ts';
import { baseurly } from '../../utils/apis.ts';
import { X_API_KEY } from '../../utils/apis.ts';
import { baseurl } from '../../utils/apis.ts';
import { useAuth } from '../../context/authContext.tsx';
import MainLayout from '../../layouts/MainLayout.tsx';
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";



const BuildingWorkstationsPage: React.FC = () => {
  const {employeeI, authReady, addSignature} = useAuth()
  const { vehicleId, buildingId } = useParams();
  const [refresh, setRefresh] = useState<boolean>(false);
  const navigate = useNavigate();

  const [processDoc, setProcessDoc] = useState<ProcessDoc | undefined>(undefined);
  const [building, setBuilding] = useState<Building | undefined>(undefined);
  const [workstations, setWorkstations] = useState<WorkStation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddProcessDoc, setShowAddProcessDoc] = useState<boolean>(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [editingWorkstationId, setEditingWorkstationId] = useState<number | null>(null);
  const [newWorkstationName, setNewWorkstationName] = useState<string>('');
  const [showAddWorkstation, setShowAddWorkstation] = useState<boolean>(false);

  useEffect(() => {
    if (!authReady) {
      return;
    }
    if (!employeeI) {
      navigate('/login', {replace: true});
      return;
    }
    if (buildingId && vehicleId) {
      fetch(`${baseurly}/buildings/${buildingId}/work_stations`,{
                    
                  method: 'GET',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-API-KEY': X_API_KEY,
                  },
                  credentials: "include",
                })
        .then((res) => res.ok ? res.json() : Promise.reject('Failed to fetch workstations'))
        .then((data) => {
          setWorkstations(data);
          // console.log(data);
          
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching workstations:', err);
          setError('Unable to load workstations');
          setLoading(false);
        });

      fetch(`${baseurly}/building/${buildingId}`,{
                    
                  method: 'GET',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-API-KEY': X_API_KEY,
                  },
                  credentials: "include",
                })
        .then((res) => res.ok ? res.json() : Promise.reject('Failed to fetch building'))
        .then((data: Building) => {
          console.log(data);
          // setProcessDoc(processDocNJMH(data.process_docs, parseInt(buildingId), parseInt(vehicleId)));
          setBuilding(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching building:', err);
          setError('Unable to load building');
          setLoading(false);
        });
    }
  }, [authReady, buildingId, employeeI, navigate, refresh, vehicleId]);

  const handleWorkstationView = (workstationId: number) => {
    navigate(`/vehicle/${vehicleId}/building/${buildingId}/workstation/${workstationId}`);
  };

  const handleEditWorkstation = (workstationId: number, currentName: string) => {
    setEditingWorkstationId(workstationId);
    setNewWorkstationName(currentName);
  };

  const handleSaveWorkstation = async (workstationId: number) => {
    try {
      const response = await fetch(`${baseurly}/work_station/${workstationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json',
                    'X-API-KEY': X_API_KEY,
         },
        body: JSON.stringify({ work_station: newWorkstationName }),
      });

      if (!response.ok) {
        throw new Error('Failed to update workstation');
      }

      const updatedWorkstations = workstations.map(ws =>
        ws.id === workstationId ? { ...ws, work_station: newWorkstationName } : ws
      );
      setWorkstations(updatedWorkstations);
      setEditingWorkstationId(null);
    } catch (error) {
      console.error('Error updating workstation:', error);
      alert('Failed to update workstation');
    }
  };

  const handleDeleteWorkstation = async (workstationId: number) => {
    try {
      const response = await fetch(`${baseurly}/work_station/${workstationId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json',
                    'X-API-KEY': X_API_KEY,
         },
      });

      if (!response.ok) {
        throw new Error('Failed to delete workstation');
      }

      const updatedWorkstations = workstations.filter(ws => ws.id !== workstationId);
      setWorkstations(updatedWorkstations);
    } catch (error) {
      console.error('Error deleting workstation:', error);
      alert('Failed to delete workstation');
    }
  };

  const handleAddWorkstation = async () => {
    if (!buildingId) {
      return alert('Please select a building first');
    }
    try {
      const response = await fetch(`${baseurly}/work_station`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
                    'X-API-KEY': X_API_KEY,
         },
        body: JSON.stringify({ work_station: newWorkstationName, building_id: parseInt(buildingId) }),
      });

      if (!response.ok) {
        throw new Error('Failed to add workstation');
      }

      const newWorkstation = await response.json();
      setWorkstations([...workstations, newWorkstation]);
      setShowAddWorkstation(false);
      setNewWorkstationName('');
    } catch (error) {
      console.error('Error adding workstation:', error);
      alert('Failed to add workstation');
    }
  };

  // Define the handleAddProcessDocClick function
  const handleAddProcessDocClick = () => {
    setShowAddProcessDoc(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmitProcessDoc = async () => {
    if (!selectedBuildingId || !file || !vehicleId) {
      alert('Please select a building and upload a file.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Upload the file first
      const uploadResponse = await fetch(`${baseurly}/upload/processDoc`, {
        method: 'POST',
        headers: { 'X-API-KEY': X_API_KEY },
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }

      const filePath = await uploadResponse.text();
      

      // Submit the process document
      const payload = {
        building_id: parseInt(selectedBuildingId),
        vehicle_model_id: parseInt(vehicleId),
        file: JSON.parse(filePath),
      };

      const submitResponse = await fetch(`${baseurly}/process_docs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
                  'X-API-KEY': X_API_KEY,
         },
        body: JSON.stringify(payload),
      });

      if (!submitResponse.ok) {
        throw new Error('Failed to submit process document');
      }

      let dataL = await submitResponse.json();
      

      alert('Process document submitted successfully!');
      setShowAddProcessDoc(false);
      setProcessDoc(dataL);
      setRefresh(!refresh);
      setSelectedBuildingId('');
      setFile(null);
    } catch (error) {
      console.error('Error submitting process document:', error);
      alert('Failed to submit process document');
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-blue-900 pb-20">
      {/* Header */}


      {/* Page Title */}
      <div className="p-6">
        <h1 className="text-white text-lg font-medium">Select a Workstation</h1>
      </div>

      {/* Search Bar */}
      <div className="px-6 mb-6">
        <div className="relative">
          <input type="text" placeholder="Search workstations..." className="w-full py-3 px-4 pr-10 rounded-full" />
          <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* Workstations List */}
      <div className="bg-white ring-2 ring-white-500 rounded-t-3xl">
        <div className="px-6 mb-6 bg-white rounded-full p-6">
          <div className="bg-white rounded-xl border p-8 flex justify-between items-center">
            <div>
              <h2 className="text-blue-900 text-xl font-bold mb-2">
                Work Stations
              </h2>
              <p className="text-gray-600 text-sm max-w-xs">
                Find clear, step-by-step instructions to help you complete your tasks efficiently and accurately.
              </p>

{/* {
  (processDoc && processDoc.id > 0) && (
    <button
                onClick={() => navigate(`/process-document/${processDoc?.id}`)}
                className="mt-4 bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 flex items-center gap-2"
              >
                <FileText size={16} />
                View Process Document
              </button>
  )
} */}
              

              {/* <button
                onClick={handleAddProcessDocClick} // Use the defined function here
                className="mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Plus size={16} />
                Add Process Document
              </button> */}
            </div>
            <img
              src={digitalOceanImageUrl("products/kayoola_coach/images/orders-coach/carousel.png")}
              alt="Bus illustration"
              className="w-27 h-24"
            />
          </div>
        </div>

        {/* Add Process Document Modal */}
        {showAddProcessDoc && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white p-6 rounded-lg w-96">
              <h2 className="text-blue-900 text-xl font-bold mb-4">Add Process Document</h2>
              <select
                value={selectedBuildingId}
                onChange={(e) => setSelectedBuildingId(e.target.value)}
                className="w-full p-2 border rounded mb-4"
              >
                <option value="">Select Building</option>
                {building && (
                  <option value={building.id}>{building.building_name}</option>
                )}
              </select>
              <input
                type="file"
                onChange={handleFileChange}
                className="w-full p-2 border rounded mb-4"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowAddProcessDoc(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitProcessDoc}
                  className="bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl p-6 mx-6">
          <h2 className="text-blue-900 text-xl font-bold mb-4">Available Workstations</h2>

          {loading ? (
            <p className="text-gray-600">Loading workstations...</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : workstations.length > 0 ? (
            <ul className="space-y-3">
              {workstations
              .sort((a, b) => a.id - b.id)
              .map((ws) => (
                <li
                  key={ws.id}
                  className="p-4 border bg-sky-200 font-semibold rounded-lg flex justify-between items-center"
                >
                  {editingWorkstationId === ws.id ? (
                    <input
                      type="text"
                      value={newWorkstationName}
                      onChange={(e) => setNewWorkstationName(e.target.value)}
                      className="p-2 border rounded"
                    />
                  ) : (
                    ws.work_station
                  )}
                  <div className="flex gap-2">
                    {editingWorkstationId === ws.id ? (
                      <>
                        <button
                          className="px-4 py-2 text-green-600 bg-white rounded hover:bg-gray-100"
                          onClick={() => handleSaveWorkstation(ws.id)}
                        >
                          <Save size={16} />
                        </button>
                        <button
                          className="px-4 py-2 text-red-600 bg-white rounded hover:bg-gray-100"
                          onClick={() => setEditingWorkstationId(null)}
                        >
                          <X size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="px-4 py-2 text-blue-900 bg-white rounded hover:bg-gray-100"
                          onClick={() => handleEditWorkstation(ws.id, ws.work_station)}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="px-4 py-2 text-blue-900 bg-white rounded hover:bg-gray-100"
                          onClick={() => handleWorkstationView(ws.id)}
                        >
                          View
                        </button>
                        <button
                          className="px-4 py-2 text-red-600 bg-white rounded hover:bg-gray-100"
                          onClick={() => handleDeleteWorkstation(ws.id)}
                        >
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600">No workstations available.</p>
          )}

          {/* Add Workstation Button */}
          <button
            onClick={() => setShowAddWorkstation(true)}
            className="mt-4 bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 flex items-center gap-2"
          >
            <Plus size={16} />
            Add Workstation
          </button>

          {/* Add Workstation Modal */}
          {showAddWorkstation && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
              <div className="bg-white p-6 rounded-lg w-96">
                <h2 className="text-blue-900 text-xl font-bold mb-4">Add Workstation</h2>
                <input
                  type="text"
                  placeholder="Workstation Name"
                  value={newWorkstationName}
                  onChange={(e) => setNewWorkstationName(e.target.value)}
                  className="w-full p-2 border rounded mb-4"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowAddWorkstation(false)}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddWorkstation}
                    className="bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav showBack={true} />
    </div>
    </MainLayout>
    
  );
};

export default BuildingWorkstationsPage;
